import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import ModuleLayout from "../../components/admin/ModuleLayout"
import Alert from "@/components/ui/alert/Alert"
import { SectionHeader } from "@/components/cccd/SectionHeader"
import { cccdAdmin, type EventPlanInput } from "@/lib/api/cccdAdminClient"
import { slugify } from "@/lib/slugify"
import { toastError } from "@/lib/toast"
import { getValidationErrorMessage } from "@/lib/errors"
import { Toggle } from "@/components/ui/Toggle"
import { LoadState } from "@/components/ui/LoadState"

type PlanType = "full" | "installments" | "flexible"

type FormState = {
  slug: string
  name: string
  description: string
  planType: PlanType
  installmentsCount: string
  minFlexibleMonths: string
  maxFlexibleMonths: string
  interestRatePercent: string
  earlyBirdDiscountPercent: string
  earlyBirdDeadlineMonth: string
  isActive: boolean
}

const emptyForm: FormState = {
  slug: "",
  name: "",
  description: "",
  planType: "full",
  installmentsCount: "",
  minFlexibleMonths: "",
  maxFlexibleMonths: "",
  interestRatePercent: "0",
  earlyBirdDiscountPercent: "0",
  earlyBirdDeadlineMonth: "",
  isActive: true,
}

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]

function toInput(form: FormState): Omit<EventPlanInput, "orderIndex"> {
  return {
    slug: form.slug,
    name: form.name,
    description: form.description,
    planType: form.planType,
    installmentsCount: form.installmentsCount ? Number(form.installmentsCount) : null,
    minFlexibleMonths: form.minFlexibleMonths ? Number(form.minFlexibleMonths) : null,
    maxFlexibleMonths: form.maxFlexibleMonths ? Number(form.maxFlexibleMonths) : null,
    interestRatePercent: Number(form.interestRatePercent) || 0,
    earlyBirdDiscountPercent: Number(form.earlyBirdDiscountPercent) || 0,
    earlyBirdDeadlineMonth: form.earlyBirdDeadlineMonth !== "" ? Number(form.earlyBirdDeadlineMonth) : null,
    isActive: form.isActive,
    // admin-list-drag-reorder: el orden se arrastra desde la lista, no se
    // elige aquí — el backend asigna el siguiente disponible al crear.
  }
}

export default function PlanFormPage() {
  const { eventId, regionalId, planId } = useParams<{ eventId?: string; regionalId?: string; planId: string }>()
  const isRegional = Boolean(regionalId)
  const parentId = (eventId ?? regionalId)!
  const isEdit = Boolean(planId)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["event-plans", isRegional ? "regional" : "event", parentId],
    queryFn: () => (isRegional ? cccdAdmin.getRegionalPlans(parentId) : cccdAdmin.getEventPlans(parentId)),
    enabled: Boolean(parentId),
  })

  const [form, setForm] = useState<FormState>(emptyForm)
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isEdit || isLoading) return
    const plan = plans.find((p) => p.id === planId)
    if (plan) {
      setForm({
        slug: plan.slug,
        name: plan.name,
        description: plan.description ?? "",
        planType: plan.planType,
        installmentsCount: plan.installmentsCount != null ? String(plan.installmentsCount) : "",
        minFlexibleMonths: plan.minFlexibleMonths != null ? String(plan.minFlexibleMonths) : "",
        maxFlexibleMonths: plan.maxFlexibleMonths != null ? String(plan.maxFlexibleMonths) : "",
        interestRatePercent: plan.interestRatePercent != null ? String(plan.interestRatePercent) : "0",
        earlyBirdDiscountPercent: plan.earlyBirdDiscountPercent != null ? String(plan.earlyBirdDiscountPercent) : "0",
        earlyBirdDeadlineMonth: plan.earlyBirdDeadlineMonth != null ? String(plan.earlyBirdDeadlineMonth) : "",
        isActive: plan.isActive ?? true,
      })
    }
  }, [isEdit, isLoading, plans, planId])

  const invalidate = () => qc.invalidateQueries({ queryKey: ["event-plans", isRegional ? "regional" : "event", parentId] })

  const createMutation = useMutation({
    mutationFn: (input: Partial<EventPlanInput>) =>
      isRegional ? cccdAdmin.createRegionalPlan(parentId, input) : cccdAdmin.createEventPlan(parentId, input),
    onSuccess: invalidate,
  })
  const updateMutation = useMutation({
    mutationFn: (input: Partial<EventPlanInput>) =>
      isRegional ? cccdAdmin.updateRegionalPlan(parentId, planId!, input) : cccdAdmin.updateEventPlan(parentId, planId!, input),
    onSuccess: invalidate,
  })

  function set(key: keyof FormState, value: string | boolean) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function goBack() {
    navigate(isRegional ? `/cccd/regionals/${parentId}` : `/cccd/events/${parentId}?tab=configuracion`)
  }

  /** Reglas por tipo de plan; devuelve el mensaje en español o null si todo está en orden. */
  function validate(): string | null {
    if (form.planType === "installments" && !(Number(form.installmentsCount) >= 1)) {
      return "Indica el número de cuotas (al menos 1) para un plan de cuotas fijas."
    }
    if (form.planType === "flexible") {
      const min = Number(form.minFlexibleMonths)
      const max = Number(form.maxFlexibleMonths)
      if (!(min >= 1) || !(max >= 1)) return "Indica el mínimo y el máximo de meses para un plan de meses flexibles."
      if (min > max) return "El mínimo de meses no puede ser mayor que el máximo."
    }
    return null
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const invalid = validate()
    if (invalid) {
      setError(invalid)
      return
    }
    setSaving(true)
    setError("")
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(toInput(form))
      } else {
        await createMutation.mutateAsync(toInput(form))
      }
      goBack()
    } catch (err: unknown) {
      const message = getValidationErrorMessage(err)
      setError(message)
      toastError(message)
      setSaving(false)
    }
  }

  const inputCls = "min-h-11 w-full border border-gray-300 dark:border-gray-700 rounded-lg p-2 text-sm bg-white dark:bg-gray-800"
  const labelCls = "mb-1 block text-xs text-gray-600 dark:text-gray-400"

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        { label: "CCCD", to: "/cccd" },
        {
          label: isRegional ? "Regionales" : "Nacionales",
          to: isRegional ? `/cccd/regionals/${parentId}` : `/cccd/events/${parentId}`,
        },
        isEdit ? "Editar plan" : "Nuevo plan",
      ]}
      title={isEdit ? "Editar plan" : "Nuevo plan"}
      subtitle="Plan de pago del evento"
      tabs={[]}
      metrics={[]}
    >
      {isEdit && isLoading ? (
        <LoadState />
      ) : (
        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <SectionHeader
              title="Identificación"
              subtitle="Cómo se llama y qué tipo de plan de pago es"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M4 12h10M4 17h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>}
              className="space-y-3"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="plan-form-name" className={labelCls}>Nombre *</label>
                  <input
                    id="plan-form-name"
                    required
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value
                      setForm((prev) => ({ ...prev, name, slug: isEdit ? prev.slug : slugify(name) }))
                    }}
                    placeholder="Nombre del plan"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Slug</label>
                  <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <span className="font-mono">{form.slug || "—"}</span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(autogenerado)</span>
                  </p>
                </div>
              </div>
              <div>
                <label htmlFor="plan-form-description" className={labelCls}>Descripción</label>
                <textarea id="plan-form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Descripción del plan" className={inputCls} />
              </div>
              <div>
                <label htmlFor="plan-form-type" className={labelCls}>Tipo de plan *</label>
                <select id="plan-form-type" value={form.planType} onChange={(e) => set("planType", e.target.value as PlanType)} className={inputCls}>
                  <option value="full">Pago único</option>
                  <option value="installments">Cuotas fijas</option>
                  <option value="flexible">Meses flexibles</option>
                </select>
              </div>
            </SectionHeader>

            <SectionHeader
              title="Configuración"
              subtitle="Cuotas, intereses y el descuento por pago anticipado"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.7" /><path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>}
              className="space-y-3"
            >
              {form.planType === "installments" && (
                <div>
                  <label htmlFor="plan-form-installments" className={labelCls}>Número de cuotas *</label>
                  <input id="plan-form-installments" required type="number" min="1" step="1" inputMode="numeric" value={form.installmentsCount} onChange={(e) => set("installmentsCount", e.target.value)} placeholder="Ej: 3" className={inputCls} />
                </div>
              )}
              {form.planType === "flexible" && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="plan-form-min-months" className={labelCls}>Mín. meses *</label>
                    <input id="plan-form-min-months" required type="number" min="1" step="1" inputMode="numeric" value={form.minFlexibleMonths} onChange={(e) => set("minFlexibleMonths", e.target.value)} placeholder="1" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="plan-form-max-months" className={labelCls}>Máx. meses *</label>
                    <input id="plan-form-max-months" required type="number" min="1" step="1" inputMode="numeric" value={form.maxFlexibleMonths} onChange={(e) => set("maxFlexibleMonths", e.target.value)} placeholder="12" className={inputCls} />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="plan-form-interest" className={labelCls}>Tasa de interés</label>
                  <div className="relative">
                    <input id="plan-form-interest" type="number" min="0" max="100" step="0.01" value={form.interestRatePercent} onChange={(e) => set("interestRatePercent", e.target.value)} placeholder="0" className={`${inputCls} pr-8`} />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">%</span>
                  </div>
                </div>
                <div>
                  <label htmlFor="plan-form-discount" className={labelCls}>Descuento por pago anticipado</label>
                  <div className="relative">
                    <input id="plan-form-discount" type="number" min="0" max="100" step="0.01" value={form.earlyBirdDiscountPercent} onChange={(e) => set("earlyBirdDiscountPercent", e.target.value)} placeholder="0" className={`${inputCls} pr-8`} />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">%</span>
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="plan-form-deadline-month" className={labelCls}>Mes límite del pago anticipado</label>
                <select id="plan-form-deadline-month" value={form.earlyBirdDeadlineMonth} onChange={(e) => set("earlyBirdDeadlineMonth", e.target.value)} className={inputCls}>
                  <option value="">Sin límite</option>
                  {MONTHS.map((m, i) => (
                    <option key={i} value={String(i)}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="pb-2">
                <Toggle checked={form.isActive} onChange={(v) => set("isActive", v)} label="Plan activo" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                El orden de este plan se define arrastrándolo en la lista de planes del evento, no aquí.
              </p>
            </SectionHeader>
          </div>

          {error ? <div className="mt-5"><Alert variant="error" title="No se pudo guardar" message={error} /></div> : null}

          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" onClick={goBack} className="min-h-11 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="min-h-11 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      )}
    </ModuleLayout>
  )
}
